import React, { useLayoutEffect, useRef, useState } from "react";
import { useCreateMessageAndResponseMutation } from "../../../api/chatApi";
import type { Message } from "./types";
import { preventOverflowOnTextarea } from "../../../products/copy-assistant/utils";
import { Button } from "../../../components/tailwind";

type ChatMessageFormProps = {
  chatId: number;
  modelName: string;
  onMessageAdded: (newMessage: Message) => void;
};

export const ChatMessageForm: React.FC<ChatMessageFormProps> = ({
  chatId,
  modelName,
  onMessageAdded,
}) => {
  const [inputText, setInputText] = useState<string>("");
  const [createMessage, { isLoading }] = useCreateMessageAndResponseMutation();
  const textareaRef = useRef<HTMLTextAreaElement>();

  useLayoutEffect(() => {
    if (textareaRef.current) {
      preventOverflowOnTextarea(textareaRef.current);
    }
  }, []);

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (!inputText.trim() || isLoading) return;

    const userMessage: Message = {
      role: "user",
      text: inputText,
      timeStamp: new Date().toISOString().replace("T", " ").slice(0, 19),
    };
    onMessageAdded(userMessage);
    setInputText("");

    try {
      const response = await createMessage({
        chat_id: chatId,
        text: inputText,
      }).unwrap();

      const responseMessage = response.response;
      const assistantMessage: Message = {
        role: "assistant",
        text: responseMessage?.text || "",
        timeStamp: responseMessage?.timestamp,
      };
      onMessageAdded(assistantMessage);
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="tw-grow-0 tw-p-4 tw-rounded-md tw-border tw-shadow-lg"
    >
      <textarea
        value={inputText}
        ref={textareaRef}
        onChange={(e) => setInputText(e.target.value)}
        className="tw-m-1 !tw-p-0 tw-resize-none tw-border !tw-ring-0"
        disabled={isLoading}
      />
      <div className="tw-flex tw-justify-between">
        <div className="tw-flex tw-gap-4">
          <button>Attach File</button>
          <select>
            <option key={modelName} value={modelName}>
              {modelName}
            </option>
          </select>
        </div>
        <Button
          type="submit"
          disabled={inputText.length === 0 || isLoading}
          variant="textual-blue"
        >
          {isLoading ? "Sending..." : "Send"}
        </Button>
      </div>
    </form>
  );
};
